/* eslint-disable no-prototype-builtins */
import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import { Link } from 'react-router-dom'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import AddIcon from '@mui/icons-material/Add'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import ReplayIcon from '@mui/icons-material/Replay'
import RefreshIcon from '@mui/icons-material/Refresh'

import FeedbackRequestItemCard from './helpers/FeedbackRequestItemCard'

import useWindowDimensions from '../../../helpers/windowDimensions'
import { standardButton, exitModalIconButton } from '../../../helpers/buttons'
import { standardSpinner } from '../../../helpers/spinners'
import { getPayload, getTokenFromLocalStorage, userIsAuthenticated, getEmailFromLocalStorage, setIsReviewerToLocalStorage } from '../../../helpers/storage'
import { profilesSortByArray, positionChangeWidthSm, positionChangeWidthMd, swipelyticsPink, swipelyticsTurquoise, navbarHeight, footerHeight, dashboardNavbarHeight } from '../../../helpers/variableDefaults'
import { selectSortCategory } from '../../../helpers/selects'
import { couldNotLoadPageError } from '../../../helpers/errors'
import { switchWithTitle } from '../../../helpers/switches'
import { modifyInDatabase } from '../../../helpers/modifyInDatabase'
import { feedbackProfileModal } from '../../../helpers/modals'
import { seoPageTags, customAnalyticsEvent } from '../../../helpers/analytics'
import { searchTextField } from '../../../helpers/textfields'
import { standardPagination } from '../../../helpers/pagination'
import { reloadView, numberWithCommas } from '../../../helpers/globalHelpers'

// Feedback Requests Page
const FeedbackRequests = (props) => {
  
  // Window Dimensions
  const { height, width } = useWindowDimensions()

  // Destructure Props
  const { viewIndex } = props
  
  // Navigate
  const navigate = useNavigate()

  // Params
  const { userId } = useParams()

  // Payload
  const payload = getPayload()

  // User
  const [user, setUser] = useState({})

  // Sorting — New Request or History
  const [filteredFeedbackRequests, setFilteredFeedbackRequests] = useState([]) // Feedback Requests after filters are added

  // Pagination
  const [activePage, setActivePage] = useState(1)
  const itemsPerPage = 24
  const pageRangeDisplayed = 3

  // Loading
  const [loading, setLoading] = useState(false)

  // Errors
  const [errors, setErrors] = useState(false)
  const [feedbackProfileErrors, setFeedbackProfileErrors] = useState(false)

  // UseEffect — Send to user profile view if User is not a Person in the estate
  useEffect(() => {
    // console.log('useEffect runs')

    if (!userIsAuthenticated()) {
      navigate('/login')
    } else if (userId !== payload.sub) {
      navigate(`/dashboard/${payload.sub}`, { state: { previousLocationPathname: window.location.pathname } })
      // reloadView()
    } else {
      const getData = async () => {
        setLoading(true)
        try {
          const { data: retrievedUser } = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/users/${payload.sub}`, {
            headers: {
              Authorization: `Bearer ${getTokenFromLocalStorage()}`,
            },
          })
          // console.log('retrieved User ->', retrievedUser)
          // console.log('retrievedUser.isReviewer ->', retrievedUser.isReviewer)

          if (!retrievedUser.isReviewer) {
            // console.log('this runs')
            setIsReviewerToLocalStorage(false)
            reloadView()
          } else if (payload.sub === retrievedUser._id) {
            // Set States
            setUser(retrievedUser)
            setFilteredFeedbackRequests(
              retrievedUser.hasOwnProperty('reviewerProfile') 
              && 'profileReviews' in retrievedUser.reviewerProfile 
              && retrievedUser.reviewerProfile.profileReviews !== null
              && retrievedUser.reviewerProfile.profileReviews.length > 0
              && retrievedUser.reviewerProfile.profileReviews.filter(profileReview => viewIndex === 0 ? !(profileReview.reviewSubmitted) : (profileReview.reviewSubmitted)).length > 0   
                ? 
                retrievedUser.reviewerProfile.profileReviews.filter(profileReview => viewIndex === 0 ? !(profileReview.reviewSubmitted) : (profileReview.reviewSubmitted))
                : 
                []
            )
            
            setErrors(false)
            setLoading(false)
          } else {
            navigate(`/`)
            setErrors(false)
            setLoading(false)
          }

        } catch (error) {
          // console.log(error)

          navigate(`/`)

          setErrors(true)
          setLoading(false)
        }
      }

      getData()
    }

  }, [])

  useEffect(() => {
    // Scroll to top of page when pagination changes
    window.scrollTo(0, 0)

  }, [activePage])


  const feedbackRequestInActivePage = (feedbackRequestIndexPlusOne) => {
    return (activePage * itemsPerPage >= feedbackRequestIndexPlusOne) 
      && (feedbackRequestIndexPlusOne >= (((activePage - 1) * itemsPerPage) + 1))
  }

  return (
    <>
      {/* Helmet — for analytics, seo, and page title changing */}
      {seoPageTags(
        'Feedback Requests'
      )}

      {/* Body */}
      {loading ?
        <Box sx={{ 
          width: '100%',
          // minHeight: `calc(100vh - ${navbarHeight} - ${footerHeight} - ${dashboardNavbarHeight})`,
          minHeight: `calc(100vh - ${navbarHeight} - ${dashboardNavbarHeight})`,
          mt: 0, mb: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', 
        }}>
          {standardSpinner()}
        </Box>
        :
        errors ? 
          <Box
            sx={{
              // backgroundColor: 'yellow',
              width: '100%',
              minHeight: `calc(100vh - ${navbarHeight} - ${footerHeight} - ${dashboardNavbarHeight})`,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', 
            }}
          >
            {couldNotLoadPageError()}
          </Box>
          :
          <Box
            sx={{
              // backgroundColor: 'green',
              pt: 3, pb: 4,
              width: '100%',
              minHeight: `calc(100vh - ${navbarHeight} - ${footerHeight} - ${dashboardNavbarHeight})`,
              display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center',
            }}
          > 
            {/* Inactive Subscription */}
            {user.hasOwnProperty('subscriptionIsActive') && !user.subscriptionIsActive &&
              <Box
                sx={{
                  backgroundColor: 'red',
                  color: 'white',
                  py: 3, px: 3,
                  width: '100%', 
                  // height: '100px',
                  fontSize: '18px', fontWeight: 'bold',
                  display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
                }}
              >
                {`Click 'Manage Subscription' in the 'Settings' tab to reactivate subscription`}
              </Box>
            }

            {/* Profiles */}
            {!user.hasOwnProperty('reviewerProfile') || !('profileReviews' in user.reviewerProfile) || (user.reviewerProfile.profileReviews === null) || user.reviewerProfile.profileReviews.length === 0 || user.reviewerProfile.profileReviews.filter(profileReview => viewIndex === 0 ? !(profileReview.reviewSubmitted) : (profileReview.reviewSubmitted)).length === 0 ?
              <Typography 
                textAlign={'center'}
                sx={{ 
                  width: '95%', maxWidth: '600px',
                  color: 'black', mt: 3, fontWeight: 'bold', 
                }}
              >
                {viewIndex === 0 ? `No profiles have been submitted for you to review yet. You will receive an email when a request occurs.` : `No profiles reviews have been completed yet.`}
              </Typography>
              :
              <Box sx={{ mt: 0, mb: 2, width: '100vw', maxWidth: '600px', minWidth: '250px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center' }}>
                
                {/* The Profiles that meet the criteria are sorted and mapped */}
                {user.hasOwnProperty('reviewerProfile') && 'profileReviews' in user.reviewerProfile && user.reviewerProfile.profileReviews !== null && filteredFeedbackRequests.length > 0 && 
                  filteredFeedbackRequests.sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)).map((profile, profileIndex) => (
                    
                    <Box 
                      key={profileIndex}
                      sx={{
                        width: '100%',
                        mb: 0,
                        display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center',
                      }}
                    >
                      {/* Feedback Requests */}
                      {
                        feedbackRequestInActivePage(profileIndex + 1) &&
                        <FeedbackRequestItemCard profile={profile} profileIndex={profileIndex} user={user} fromComponent={'dashboard'} viewIndex={viewIndex} />
                      }
                    </Box>
                  ))
                }

                {/* Message if no profiles */}
                {filteredFeedbackRequests.length === 0 &&
                  <Typography 
                    sx={{ 
                      color: 'black', mt: 4, fontWeight: 'bold', 
                    }}
                  >
                    {`No profile review requests have been ${viewIndex === 1 ? `completed.` : `received. You will receive an email when a feedback request has been made.`}`}
                  </Typography>
                }

                {/* Pagination */}
                {filteredFeedbackRequests.length > itemsPerPage &&
                  standardPagination(activePage, setActivePage, itemsPerPage, pageRangeDisplayed, filteredFeedbackRequests.length, user.subscriptionIsActive, width)
                }

              </Box>
            } 
          </Box>
      }
    </>
  )
}

export default FeedbackRequests